import React, { useState, useEffect } from "react";
import { Box, Checkbox, Grid, InputAdornment, ListItemText, MenuItem, TextField, Typography } from "@mui/material";
import { Search } from "@mui/icons-material";
import CustomFormControl from "../../../../alert-center/Dropdown/CustomFormControl";
import CustomOutlinedInput from "components/CustomOutlinedInput/CustomOutlinedInput";
import {
  BIDDING_STRATEGIES,
} from "../../../constants/biddingStrategies";
import { useSelector } from "react-redux";
import {
  validateBiddingStrategy,
  validateCampaignName,
} from "../../../Utils/validators/campaignSettingsValidators";
import useMetaHotels from "../../../Utils/useMetaHotels";

const CampaignSettingsDetails = ({
  biddingStrategy,
  setBiddingStrategy,
  campaignName,
  setCampaignName,
  campaignBudget,
  setCampaignBudget,
  errors,
  currencySymbol,
  campaignId = null,
  isEdit = false,
  selectedHotels = [],
  setSelectedHotels,
  isMultiHotelEnabled = false,
  lockedHotels = [],
}) => {
  const accountId = useSelector((state) => state?.account?.account?.account_id);
  const { hotel_id = [] } = useSelector((state) => state?.account);
  const currency = currencySymbol;

  const [campaignNameStatus, setCampaignNameStatus] = useState(null);
  const [maxCpcError, setMaxCpcError] = useState(null);
  const { hotelOptions } = useMetaHotels(accountId);
  const [hotelSearchQuery, setHotelSearchQuery] = useState("");

  const handleMaxCpcBlur = () => {
    if (biddingStrategy !== "MAXIMIZE_CONVERSIONS") return;
    if (campaignBudget !== "" && isNaN(Number(campaignBudget))) {
      setMaxCpcError("Max CPA must be a valid number");
    } else {
      setMaxCpcError(null);
      delete errors?.campaignTargetBudget;
    }
  };

  const handleMaxCpcChange = (e) => {
    setCampaignBudget(e.target.value);
    if (maxCpcError) setMaxCpcError(null);
    if (errors?.campaignTargetBudget) delete errors.campaignTargetBudget;
  };

  const handleCampaignNameBlur = async (text) => {
    if (!text.trim()) return;
    const result = await validateCampaignName(
      accountId,
      hotel_id?.[0],
      text,
      campaignId
    );
    if (result.success) {
      delete errors?.campaignName;
    }
    setCampaignNameStatus(
      result.success
        ? { success: true, error: null }
        : { success: false, error: result.error || "Invalid campaign name" }
    );
  };

  const handleBiddingStrategyBlur = async (val) => {
    const result = await validateBiddingStrategy({
      bidding_strategy_option: val,
    });
    if (result.success) {
      delete errors?.biddingStrategy;
    }
  };

  const handleBiddingStrategyChange = (event) => {
    setBiddingStrategy(event.target.value);
    handleBiddingStrategyBlur(event.target.value);
  };

  return (
    <Box className="campaignSettingsDetails">
      <Box p={2}>
        <Grid container gap={"56px"} alignItems="flex-start">
          {isEdit && (
            <Grid item>
              <Typography
                className="sectionTitle"
                style={{ margin: 0, marginBottom: "8px", visibility: "hidden" }}
              >
                Campaign Name
              </Typography>
              <CustomOutlinedInput
                minWidth={"330px"}
                placeholder="Campaign Name"
                variant="outlined"
                value={campaignName}
                disabled={true}
                FormHelperTextProps={{
                  className: "custom-helper-text",
                }}
                helperText="Campaign Name"
                InputProps={{
                  sx: { 
                    height: "28px", 
                    backgroundColor: "#f5f5f5",
                    "& .Mui-disabled": {
                      WebkitTextFillColor: "#9e9e9e",
                    }
                  },
                }}
              />
            </Grid>
          )}
          <Grid item>
            <Typography
              className="sectionTitle"
              style={{ margin: 0, marginBottom: "8px" }}
            >
              Select Your Bidding Strategy
            </Typography>
            <CustomFormControl
              minWidth={"240px"}
              sx={{ width: "240px" }}
              value={biddingStrategy}
              onChange={handleBiddingStrategyChange}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#bab7b7",
                      }}
                    >
                      Choose a bidding strategy
                    </span>
                  );
                }
                return BIDDING_STRATEGIES[selected];
              }}
              required
              error={errors.biddingStrategy}
              helperText={errors.biddingStrategy || "Bidding Strategy"}
              helperTextFontSize={"10px"}
              paperProps={{
                style: {
                  borderRadius: "10px",
                },
              }}
            >
              {Object.entries(
                BIDDING_STRATEGIES
              ).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#373F41",
                      marginLeft: "4px",
                    }}
                  >
                    {value}
                  </span>
                </MenuItem>
              ))}
            </CustomFormControl>
          </Grid>
          {biddingStrategy === "MAXIMIZE_CONVERSIONS" && (
            <Grid item>
              <Typography
                className="sectionTitle"
                style={{ margin: 0, marginBottom: "8px", visibility: "hidden" }}
              >
                Max CPA
              </Typography>
              <CustomOutlinedInput
                minWidth={"20px"}
                sx={{ width: "100px" }}
                placeholder="0"
                type="number"
                value={campaignBudget}
                onChange={handleMaxCpcChange}
                onBlur={handleMaxCpcBlur}
                inputProps={{ min: 0, step: "any" }}
                error={!!(maxCpcError || errors?.campaignTargetBudget)}
                helperText={maxCpcError || errors?.campaignTargetBudget || "Max CPA"}
              />
            </Grid>
          )}
          {isMultiHotelEnabled && (
          <Grid item>
            <Typography
              className="sectionTitle"
              style={{ margin: 0, marginBottom: "8px" }}
            >
              Select a Hotel
            </Typography>
            <CustomFormControl
              minWidth={"410px"}
              sx={{ 
                width: "410px",
              }}
              multiple
              value={selectedHotels}
              onChange={(e) => {
                const value = e.target.value;
                if (Array.isArray(value) && value.includes("__select_all__")) return;
                const newValue = typeof value === "string" ? value.split(",") : value;
                // Ensure locked hotels cannot be removed (both in create and edit mode)
                if (lockedHotels.length > 0) {
                  const lockedSet = lockedHotels.map(String);
                  const ensuredValue = [...new Set([...lockedSet, ...newValue.map(String)])];
                  setSelectedHotels(ensuredValue);
                } else {
                  setSelectedHotels(newValue);
                }
              }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected || selected.length === 0) {
                  return (
                    <span style={{ fontSize: "12px", color: "#bab7b7" }}>
                      Select hotels
                    </span>
                  );
                }
                const selectedNames = selected.map((id) => {
                  const hotel = hotelOptions.find((h) => String(h.hotel_id) === String(id));
                  return hotel ? hotel.hotel_name : id;
                });
                if (selectedNames.length <= 2) {
                  return selectedNames.join(", ");
                }
                return `${selectedNames[0]}, ${selectedNames[1]} and ${selectedNames.length - 2} others selected`;
              }}
              helperText="Hotel"
              helperTextFontSize={"10px"}
              MenuProps={{
                autoFocus: false,
              }}
            >
              <MenuItem
                disableRipple
                dense
                onKeyDown={(e) => e.stopPropagation()}
                sx={{ 
                  position: "sticky", 
                  top: 0, 
                  zIndex: 1, 
                  backgroundColor: "#fff",
                  "&:hover": { backgroundColor: "#fff" },
                  "&.Mui-focusVisible": { backgroundColor: "#fff" },
                  padding: "8px 12px",
                }}
              >
                <TextField
                  size="small"
                  placeholder="Search hotels..."
                  value={hotelSearchQuery}
                  onChange={(e) => setHotelSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 16, color: "#bab7b7" }} />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                  sx={{
                    "& .MuiInputBase-root": {
                      fontSize: "12px",
                      fontFamily: "Inter",
                      height: "30px",
                      borderRadius: "6px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                  }}
                />
              </MenuItem>
              {/* Select All checkbox */}
              <MenuItem
                disableRipple
                dense
                value="__select_all__"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const filteredHotels = hotelOptions.filter((hotel) =>
                    !hotelSearchQuery ||
                    hotel.hotel_name.toLowerCase().includes(hotelSearchQuery.toLowerCase()) ||
                    hotel.hotel_id.includes(hotelSearchQuery)
                  );
                  const filteredIds = filteredHotels.map((h) => String(h.hotel_id));
                  const allSelected = filteredIds.every((id) => selectedHotels.map(String).includes(id));
                  if (allSelected) {
                    // Keep locked hotels even when deselecting all
                    if (lockedHotels.length > 0) {
                      const lockedSet = lockedHotels.map(String);
                      setSelectedHotels(selectedHotels.filter((id) => lockedSet.includes(String(id)) || !filteredIds.includes(String(id))));
                    } else {
                      setSelectedHotels(selectedHotels.filter((id) => !filteredIds.includes(String(id))));
                    }
                  } else {
                    const merged = [...new Set([...selectedHotels.map(String), ...filteredIds])];
                    setSelectedHotels(merged);
                  }
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                }}
                sx={{
                  position: "sticky",
                  top: 42,
                  zIndex: 1,
                  backgroundColor: "#fff",
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                  borderBottom: "1px solid #e0e0e0",
                  padding: "4px 12px",
                }}
              >
                <Checkbox
                  size="small"
                  checked={
                    hotelOptions.filter((hotel) =>
                      !hotelSearchQuery ||
                      hotel.hotel_name.toLowerCase().includes(hotelSearchQuery.toLowerCase()) ||
                      hotel.hotel_id.includes(hotelSearchQuery)
                    ).length > 0 &&
                    hotelOptions
                      .filter((hotel) =>
                        !hotelSearchQuery ||
                        hotel.hotel_name.toLowerCase().includes(hotelSearchQuery.toLowerCase()) ||
                        hotel.hotel_id.includes(hotelSearchQuery)
                      )
                      .every((hotel) => selectedHotels.map(String).includes(String(hotel.hotel_id)))
                  }
                  indeterminate={
                    hotelOptions.filter((hotel) =>
                      !hotelSearchQuery ||
                      hotel.hotel_name.toLowerCase().includes(hotelSearchQuery.toLowerCase()) ||
                      hotel.hotel_id.includes(hotelSearchQuery)
                    ).some((hotel) => selectedHotels.map(String).includes(String(hotel.hotel_id))) &&
                    !hotelOptions
                      .filter((hotel) =>
                        !hotelSearchQuery ||
                        hotel.hotel_name.toLowerCase().includes(hotelSearchQuery.toLowerCase()) ||
                        hotel.hotel_id.includes(hotelSearchQuery)
                      )
                      .every((hotel) => selectedHotels.map(String).includes(String(hotel.hotel_id)))
                  }
                  sx={{
                    padding: "2px",
                    color: "#BAB7B7",
                    "&.Mui-checked": { color: "#61D5A9" },
                    "&.MuiCheckbox-indeterminate": { color: "#61D5A9" },
                    "& .MuiSvgIcon-root": { height: "16px", width: "16px" },
                  }}
                />
                <ListItemText
                  primary="Select All"
                  primaryTypographyProps={{ fontSize: "12px", color: "#373F41", fontWeight: 500 }}
                />
              </MenuItem>
              {hotelOptions
                .filter((hotel) =>
                  !hotelSearchQuery ||
                  hotel.hotel_name.toLowerCase().includes(hotelSearchQuery.toLowerCase()) ||
                  hotel.hotel_id.includes(hotelSearchQuery)
                )
                .map((hotel) => {
                  const isLocked = lockedHotels.map(String).includes(String(hotel.hotel_id));
                  return (
                    <MenuItem
                      key={hotel.hotel_id}
                      value={String(hotel.hotel_id)}
                      disabled={isLocked}
                      sx={{
                        "&.Mui-selected": {
                          backgroundColor: "transparent !important",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04) !important",
                        },
                        "&.Mui-selected.Mui-focusVisible": {
                          backgroundColor: "transparent !important",
                        },
                        ...(isLocked && {
                          opacity: "0.7 !important",
                        }),
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={selectedHotels.map(String).includes(String(hotel.hotel_id))}
                        disabled={isLocked}
                        sx={{
                          padding: "2px",
                          color: "#BAB7B7",
                          "&.Mui-checked": { color: "#61D5A9" },
                          "&.Mui-disabled.Mui-checked": { color: "#61D5A9" },
                          "& .MuiSvgIcon-root": { height: "16px", width: "16px" },
                        }}
                      />
                      <ListItemText
                        primary={hotel.hotel_name}
                        primaryTypographyProps={{ fontSize: "12px", color: isLocked ? "#9e9e9e" : "#373F41" }}
                      />
                    </MenuItem>
                  );
                })}
            </CustomFormControl>
          </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default CampaignSettingsDetails;